import { useEffect, useRef, useState } from "react";
import Image from "@Components/Image";
import styles from "./TitleStamp.module.scss";
import stampFrame from "./assets/stamp-frame.svg";
import wrapperHOC from "@Utils/wrapperHOC";
import { componentAsset } from "@Utils/getStaticAssetPath";

// Images come from the static-assets bucket, never `import`ed — see `componentAsset`.
const asset = componentAsset("TitleStamp");

const city1 = asset("city-1.jpg");
const city2 = asset("city-2.jpg");
const city3 = asset("city-3.jpg");
const city4 = asset("city-4.jpg");
const city5 = asset("city-5.jpg");
const city6 = asset("city-6.jpg");

const CITIES = [city1, city2, city3, city4, city5, city6];

// While hovered, the card flips to the next city, holds briefly, flips again.
// Keep this a little longer than the flip animation so each city rests a moment.
const CYCLE_MS = 900;

/**
 * A postage-stamp graphic (frame from the Figma SVG) inline in the hero
 * headline. At rest it shows one city photo. On hover it enlarges and the photo
 * page-flips through the cities (flip → new city → brief hold → flip again).
 * Clicking while hovering flips to the next city immediately.
 */
const TitleStamp = () => {
  const [index, setIndex] = useState(0);
  const [hovered, setHovered] = useState(false);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const rootRef = useRef<HTMLSpanElement>(null);

  const nextCity = () => setIndex((prev) => (prev + 1) % CITIES.length);

  // While hovered, keep flipping to the next city on a timer.
  useEffect(() => {
    if (!hovered) return undefined;
    timerRef.current = setInterval(nextCity, CYCLE_MS);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [hovered]);

  /**
   * Dismissal for pointers that cannot un-hover.
   *
   * ⚠️  A TOUCH DEVICE NEVER SENDS mouseleave. Tapping the stamp fires an emulated
   * `mouseenter`, so `hovered` goes true, the stamp scales 2.6x and starts cycling —
   * and then nothing ever turns it off, because the finger does not "leave" the way a
   * cursor does. It stayed enlarged until the page was reloaded (QA, mweb).
   *
   * So while it is open we listen for the two gestures that mean "done with this":
   *
   *   · a pointerdown outside the stamp — a tap ON it still flips the city, which is
   *     why this tests containment rather than closing on any press;
   *   · a scroll, but only where hover is not a real input. On a mouse the cursor
   *     naturally leaves the element as the page moves and `onMouseLeave` already
   *     handles it, so closing on scroll there would cut a hover short mid-gesture.
   */
  useEffect(() => {
    if (!hovered || typeof window === "undefined") return undefined;

    const close = () => setHovered(false);
    const onPointerDown = (event: PointerEvent) => {
      const root = rootRef.current;
      if (root && event.target instanceof Node && !root.contains(event.target)) close();
    };

    // `(hover: hover)` is false on touch-only pointers, which are exactly the ones
    // that cannot deliver mouseleave.
    const hoverIsReal = window.matchMedia?.("(hover: hover)").matches ?? true;

    document.addEventListener("pointerdown", onPointerDown);
    if (!hoverIsReal) window.addEventListener("scroll", close, { passive: true });

    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      window.removeEventListener("scroll", close);
    };
  }, [hovered]);

  // Click while hovering → change the city instantly (and reset the timer so the
  // next auto-flip is a full interval away).
  const handleClick = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    nextCity();
    if (hovered) timerRef.current = setInterval(nextCity, CYCLE_MS);
  };

  return (
    <span
      ref={rootRef}
      className={`${styles.stamp} ${hovered ? styles.isHovered : ""}`}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={handleClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          handleClick();
        }
      }}
    >
      <span className={styles.inner}>
        {/* The whole stamp (frame + photo) page-flips as one unit. Keyed so it
            re-mounts and replays the flip each time the city changes. */}
        <span key={index} className={styles.card}>
          {/* Perforated white backing sits BEHIND the photo, so its scalloped
              edge shows around the photo (which fills the window). */}
          <Image
            src={stampFrame}
            alt=""
            className={styles.frame}
            width={120}
            height={118}
            isEagerLoad
          />
          <span className={styles.window}>
            <Image
              src={CITIES[index]}
              alt=""
              className={styles.photo}
              width={120}
              height={120}
              isEagerLoad
            />
          </span>
        </span>
      </span>

      {/* Preload the rest so flips are instant (hidden, no layout). */}
      <span className={styles.preload} aria-hidden="true">
        {CITIES.map((city, i) => (
          <Image
            // eslint-disable-next-line react/no-array-index-key
            key={i}
            src={city}
            alt=""
            width={1}
            height={1}
            isEagerLoad
          />
        ))}
      </span>
    </span>
  );
};

export default wrapperHOC(TitleStamp, {
  componentName: "TitleStamp",
  showForChina: true,
});
